import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { RadioGroup } from "@mui/material"

import Dialog from "@/components/dialog/Dialog"
import Radio from "@/components/form/radio/Radio"
import {
  DECIMAL_POINT_ITEMS,
  DEFAULT_EXPORT_TYPE,
  EXPORT_TYPE,
  VALUE_POSITION_TYPE,
} from "@/app/report/common/constants"

import "./exportChartDialog.scss"

const above_plots = "/images/chart_above_plots.svg"
const hide_values = "/images/chart_hide_values.svg"
const separate_table = "/images/chart_separate_table.svg"

export default function ExportChartDialog({
  open,
  setOpen,
  decimalPoints,
  onChangeDecimalPoints,
  annotationOptions,
  onChangeAnnotationOptions,
  onConfirm,
  exportOptions,
  exportOption,
  onChangeExportOption,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  decimalPoints: number
  onChangeDecimalPoints: (value: number) => void
  annotationOptions: VALUE_POSITION_TYPE
  onChangeAnnotationOptions: (value: VALUE_POSITION_TYPE) => void
  onConfirm: (exportType?: string) => void
  exportOptions?: Array<string>
  exportOption?: string
  onChangeExportOption?: (value: string) => void
}) {
  const [internalExportOption, setInternalExportOption] = useState<string>(EXPORT_TYPE.PDF)

  useEffect(() => {
    if (open) {
      setInternalExportOption(EXPORT_TYPE.PDF)
      onChangeExportOption?.(EXPORT_TYPE.PDF)
    }
  }, [open])

  const currentExportOption = exportOption ?? internalExportOption
  const handleExportOptionChange = (value: string) => {
    if (onChangeExportOption) {
      onChangeExportOption(value)
    } else {
      setInternalExportOption(value)
    }
  }

  const { t } = useTranslation()

  const annotationOptionsItems = [
    { label: t("hideValues"), value: VALUE_POSITION_TYPE.HIDE_VALUES, image: hide_values },
    {
      label: t("abovePlots"),
      value: VALUE_POSITION_TYPE.IN_CHART_EACH_POSITION,
      image: above_plots,
    },
    {
      label: t("separateTable"),
      value: VALUE_POSITION_TYPE.ABOVE,
      image: separate_table,
    },
  ]

  const onCloseDialog = () => {
    setOpen(false)
    onChangeDecimalPoints(1) // Reset to default
    onChangeAnnotationOptions(VALUE_POSITION_TYPE.HIDE_VALUES) // Reset to default
    setInternalExportOption(EXPORT_TYPE.PDF) // Reset internal state
  }

  const showTypeSelector = exportOptions && exportOptions.length > 1

  return (
    <Dialog
      open={open}
      title={t("exportData")}
      onClose={onCloseDialog}
      onConfirm={() => {
        onConfirm(currentExportOption)
        onCloseDialog()
      }}
      maxWidth="600px"
      confirmTextKey="button.export">
      <div className="ExportChartDialogCont">
        {showTypeSelector && (
          <div className="export-chart-option-item">
            <p>{t("type")}</p>
            <RadioGroup row>
              {DEFAULT_EXPORT_TYPE.filter((i) => exportOptions.includes(i.value)).map((item) => (
                <Radio
                  key={item.value}
                  value={item.value}
                  label={item.label}
                  checked={item.value === currentExportOption}
                  onChange={() => handleExportOptionChange(item.value)}
                />
              ))}
            </RadioGroup>
          </div>
        )}

        {currentExportOption === EXPORT_TYPE.PDF && (
          <div className="export-chart-pdf-options">
            <div className="export-chart-option-item">
              <p>{t("annotationOptions")}</p>
              <RadioGroup row>
                {annotationOptionsItems.map((item) => (
                  <AnnotationRadioItem
                    key={item.value}
                    item={item}
                    checked={item.value === annotationOptions}
                    onChange={() => onChangeAnnotationOptions(item.value)}
                  />
                ))}
              </RadioGroup>
            </div>

            <div className="export-chart-option-item">
              <p>{t("decimalPoint")}</p>
              <RadioGroup row>
                {DECIMAL_POINT_ITEMS.map((item) => (
                  <Radio
                    key={item.value}
                    value={item.value}
                    label={item.label}
                    checked={item.value === decimalPoints}
                    onChange={() => onChangeDecimalPoints(item.value)}
                  />
                ))}
              </RadioGroup>
            </div>
          </div>
        )}

        {currentExportOption === EXPORT_TYPE.REPORT_BUILDER && (
          <div className="export-chart-option-item">
            <p className="report-builder-description">
              {t(
                "reportBuilderDesc",
                "Create a custom report by arranging sections on a canvas. You can resize, reorder, and export as PDF.",
              )}
            </p>
          </div>
        )}
      </div>
    </Dialog>
  )
}

function AnnotationRadioItem({
  item,
  checked,
  onChange,
}: {
  item: { value: VALUE_POSITION_TYPE; label: string; image: string }
  checked: boolean
  onChange: (value: VALUE_POSITION_TYPE) => void
}) {
  return (
    <div className="annotation-radio-item">
      <Radio
        value={item.value}
        label={item.label}
        checked={checked}
        onChange={() => onChange(item.value)}
      />
      <img src={item.image} alt={item.label} width={160} height={160} />
    </div>
  )
}
